import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { getFounder, adminUpdateFounder, mediaUrl } from "@/lib/api";
import { AdminShell } from "@/components/admin/AdminShell";
import { MediaPickerDialog } from "@/components/admin/MediaPickerDialog";
import { toast } from "sonner";

const inputStyle = { background: "var(--surface)", borderColor: "var(--card-border)", color: "var(--text)" };
const Field = ({ label, children }) => (<div><label className="mb-1.5 block text-xs" style={{ color: "var(--muted)" }}>{label}</label>{children}</div>);

export default function AdminFounder() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["founder"], queryFn: getFounder });
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => { if (data) setF({ beliefs: [], timeline: [], quotes: [], social: {}, ...data }); }, [data]);

  if (isLoading || !f) return <AdminShell title="Leadership"><div className="h-64 animate-pulse rounded-[14px] border" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }} /></AdminShell>;

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setArrItem = (key, i, field, v) => setF((p) => ({ ...p, [key]: p[key].map((it, idx) => idx === i ? { ...it, [field]: v } : it) }));
  const addArrItem = (key, tpl) => setF((p) => ({ ...p, [key]: [...(p[key] || []), tpl] }));
  const delArrItem = (key, i) => setF((p) => ({ ...p, [key]: p[key].filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try { await adminUpdateFounder(f); await qc.invalidateQueries({ queryKey: ["founder"] }); toast.success("Leadership updated"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const portrait = mediaUrl(f.portrait);

  return (
    <AdminShell title="Leadership" actions={
      <button onClick={save} disabled={saving} data-testid="admin-founder-save-button" className="inline-flex items-center gap-2 rounded-[10px] px-4 py-2 text-sm font-medium disabled:opacity-60" style={{ background: "var(--accent)", color: "var(--accent-fg)" }}>
        {saving && <Loader2 size={15} className="animate-spin" />} Save Changes
      </button>
    }>
      <div className="max-w-3xl space-y-8">
        <section className="rounded-[14px] border p-6" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
          <h2 className="mb-4 text-sm font-semibold" style={{ color: "var(--text)" }}>Profile</h2>
          <div className="flex items-center gap-4">
            <div className="h-20 w-16 overflow-hidden rounded-md border" style={{ borderColor: "var(--card-border)", background: "var(--bg-elev-1)" }}>
              {portrait ? <img src={portrait} alt="" className="h-full w-full object-cover" /> : <span className="flex h-full items-center justify-center text-xs" style={{ color: "var(--muted)" }}>None</span>}
            </div>
            <div className="flex-1 space-y-2">
              <button type="button" onClick={() => setPickerOpen(true)} className="rounded-[10px] border px-3.5 py-2 text-sm" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}>Choose Portrait</button>
              <input value={typeof f.portrait === "string" && f.portrait.startsWith("http") ? f.portrait : ""} onChange={(e) => set("portrait", e.target.value)} placeholder="or paste image URL" className="w-full rounded-[10px] border px-3.5 py-2 text-xs outline-none" style={inputStyle} />
            </div>
          </div>
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Name"><input value={f.name || ""} onChange={(e) => set("name", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} /></Field>
            <Field label="Designation"><input value={f.designation || ""} onChange={(e) => set("designation", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} /></Field>
          </div>
          <div className="mt-4"><Field label="LinkedIn URL"><input value={f.social?.linkedin || ""} onChange={(e) => set("social", { ...f.social, linkedin: e.target.value })} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} /></Field></div>
          <div className="mt-4"><Field label="Biography"><textarea rows={5} value={f.bio || ""} onChange={(e) => set("bio", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} /></Field></div>
          <div className="mt-4"><Field label="Vision"><textarea rows={3} value={f.vision || ""} onChange={(e) => set("vision", e.target.value)} className="w-full rounded-[10px] border px-3.5 py-2.5 text-sm outline-none" style={inputStyle} /></Field></div>
        </section>

        {[{ key: "beliefs", title: "Core Beliefs", tpl: { title: "", text: "" }, fields: [["title", "Title"], ["text", "Text"]] },
          { key: "timeline", title: "Timeline", tpl: { year: "", title: "", text: "" }, fields: [["year", "Label"], ["title", "Title"], ["text", "Text"]] },
          { key: "quotes", title: "Quotes", tpl: { text: "" }, fields: [["text", "Quote"]] }].map((sec) => (
          <section key={sec.key} className="rounded-[14px] border p-6" style={{ background: "var(--surface)", borderColor: "var(--card-border)" }}>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-sm font-semibold" style={{ color: "var(--text)" }}>{sec.title}</h2>
              <button onClick={() => addArrItem(sec.key, { ...sec.tpl })} className="inline-flex items-center gap-1.5 rounded-[10px] border px-3 py-1.5 text-xs" style={{ borderColor: "var(--card-border)", color: "var(--text)" }}><Plus size={13} /> Add</button>
            </div>
            <div className="space-y-3">
              {(f[sec.key] || []).map((it, i) => (
                <div key={i} className="rounded-[10px] border p-3" style={{ borderColor: "var(--card-border)" }}>
                  <div className="flex items-start gap-2">
                    <div className="flex-1 space-y-2">
                      {sec.fields.map(([fk, flabel]) => (
                        fk === "text" ? (
                          <textarea key={fk} rows={2} value={it[fk] || ""} placeholder={flabel} onChange={(e) => setArrItem(sec.key, i, fk, e.target.value)} className="w-full rounded-[8px] border px-3 py-2 text-sm outline-none" style={inputStyle} />
                        ) : (
                          <input key={fk} value={it[fk] || ""} placeholder={flabel} onChange={(e) => setArrItem(sec.key, i, fk, e.target.value)} className="w-full rounded-[8px] border px-3 py-2 text-sm outline-none" style={inputStyle} />
                        )
                      ))}
                    </div>
                    <button onClick={() => delArrItem(sec.key, i)} className="inline-flex h-8 w-8 items-center justify-center rounded-md border" style={{ borderColor: "var(--card-border)", color: "var(--danger)" }}><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
              {(f[sec.key] || []).length === 0 && <p className="text-xs" style={{ color: "var(--muted)" }}>No items yet.</p>}
            </div>
          </section>
        ))}
      </div>
      <MediaPickerDialog open={pickerOpen} onOpenChange={setPickerOpen} onSelect={(id) => set("portrait", id)} />
    </AdminShell>
  );
}
